import { useEffect, type ReactNode } from 'react';
import { Logo } from '../../components/Logo';
import { RIDGE_HEIGHT, RIDGE_TILE, ridgePath } from '../../components/ridges';
import { cn } from '../../lib/cn';

const TILE = RIDGE_TILE;
const HEIGHT = RIDGE_HEIGHT;

export { ridgePath };

const LAYERS = [
  {
    ys: [150, 96, 128, 72, 118, 98, 140, 84],
    className:
      'text-[color-mix(in_srgb,var(--color-brand-500)_28%,var(--color-mist))] animate-ridge-slow',
  },
  {
    ys: [192, 148, 176, 122, 166, 138, 186, 134, 170],
    className: 'text-[color-mix(in_srgb,var(--color-brand)_50%,var(--color-mist))] animate-ridge',
  },
  {
    ys: [252, 214, 236, 198, 242, 220, 256, 208],
    className: 'text-valley animate-ridge-fast',
  },
];

/** Layered ridges drifting in parallax, with a sun that rises as `progress` (0–1) grows. */
export function Ridges({ progress = 0.5 }: { progress?: number }) {
  return (
    <div
      className="pointer-events-none absolute inset-x-0 bottom-0 h-[42vh] min-h-56 overflow-hidden"
      aria-hidden
      data-testid="ridges"
    >
      <div
        className="absolute left-1/2 size-40 -translate-x-1/2 transition-[bottom] duration-1000 sm:size-56"
        style={{ bottom: `${8 + progress * 42}%` }}
      >
        <div className="absolute inset-0 animate-glow rounded-full bg-sunrise-400/30 blur-2xl motion-reduce:animate-none" />
        <div className="absolute inset-[30%] rounded-full bg-sunrise-400 shadow-[0_0_60px_20px_rgba(224,164,59,0.35)]" />
      </div>
      {LAYERS.map((layer) => (
        <svg
          key={layer.className}
          viewBox={`0 0 ${TILE * 2} ${HEIGHT}`}
          preserveAspectRatio="none"
          className={cn(
            'absolute bottom-0 left-0 h-full w-[200%] motion-reduce:animate-none',
            layer.className,
          )}
          focusable="false"
        >
          <path d={ridgePath(layer.ys)} fill="currentColor" />
        </svg>
      ))}
    </div>
  );
}

/** Full-screen gate layout: dawn sky, logo, content card, ridges along the bottom. */
export function SiteShell({
  title,
  progress,
  children,
}: {
  /** document title prefix */
  title: string;
  progress?: number;
  children: ReactNode;
}) {
  useEffect(() => {
    const previous = document.title;
    document.title = `${title} · ShenandoahFirst`;
    return () => {
      document.title = previous;
    };
  }, [title]);

  return (
    <div className="relative flex min-h-dvh flex-col overflow-hidden bg-linear-to-b from-brand-50 via-mist to-sunrise-50">
      <Ridges progress={progress} />
      <main className="relative z-10 mx-auto flex w-full max-w-2xl flex-1 flex-col items-center px-4 pt-10 pb-[34vh] text-center md:pt-16">
        <Logo />
        <div className="mt-10 flex w-full animate-rise flex-col items-center motion-reduce:animate-none md:mt-14">
          {children}
        </div>
      </main>
    </div>
  );
}
